/**
 * ChatInterface Component - Main conversation interface with AI assistant
 *
 * This component provides the primary interface for conversing with AI models
 * about trip planning, with context-aware responses and real-time interaction.
 *
 * Features:
 * - Real-time conversation display
 * - Message input and sending
 * - Typing indicators and status updates
 * - Message formatting and syntax highlighting
 * - Conversation history scrolling
 * - Model selection and settings
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { aiChatService } from '../../services/aiChatService.js';
import { eventBridgeService, WaitingIndicator } from '@trip-planner-monorepo/shared';

const ChatInterface = ({ userId, tripId, conversationId, onModelChange }) => {
  // Component state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [currentModel, setCurrentModel] = useState('gpt-3.5-turbo');
  const [conversation, setConversation] = useState(null);
  const [error, setError] = useState(null);

  // Refs for DOM manipulation
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  /**
   * Initialize conversation and load messages
   */
  useEffect(() => {
    if (conversationId) {
      loadConversation();
    } else {
      startNewConversation();
    }

    // Subscribe to AI response events
    const unsubscribeResponse = eventBridgeService.subscribe('AI_RESPONSE_RECEIVED', handleAiResponse);
    const unsubscribeError = eventBridgeService.subscribe('AI_RESPONSE_FAILED', handleAiError);

    return () => {
      unsubscribeResponse();
      unsubscribeError();
    };
  }, [conversationId, userId, tripId]);

  /**
   * Auto-scroll to bottom when new messages arrive
   */
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(async () => {
    try {
      setIsLoading(true);
      const newConversationId = await aiChatService.startConversation(userId, tripId, {
        model: currentModel
      });

      setConversation({ id: newConversationId });
      setMessages([]);
      setError(null);

      // Add welcome message
      const welcomeMessage = {
        id: 'welcome',
        role: 'assistant',
        content: `Hello! I'm your AI travel assistant. I have access to your trip details and can help you with planning, suggestions, and questions about your ${tripId ? 'current trip' : 'travel plans'}. What would you like to know?`,
        timestamp: new Date().toISOString(),
        isWelcome: true
      };

      setMessages([welcomeMessage]);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [userId, tripId, currentModel]);

  /**
   * Load existing conversation
   */
  const loadConversation = useCallback(async () => {
    try {
      setIsLoading(true);
      // TODO: Load conversation from service
      // const conversationData = await aiChatService.getConversation(conversationId);
      // setConversation(conversationData);
      // setMessages(conversationData.messages);
      // setCurrentModel(conversationData.model);

      // Mock conversation loading
      setConversation({ id: conversationId });
      setMessages([]);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading || !conversation) {
      return;
    }

    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsTyping(true);
    setError(null);

    // Add user message to display immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
      userId
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      // Send message to AI service
      eventBridgeService.publish('CHAT_MESSAGE_SENT', {
        conversationId: conversation.id,
        message: messageText,
        userId,
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      setError(err.message);
      setIsTyping(false);
    }
  }, [inputMessage, isLoading, conversation, userId]);

  /**
   * Handle AI response event
   */
  const handleAiResponse = useCallback((event) => {
    if (event.payload.conversationId === conversation?.id) {
      const aiMessage = {
        id: event.payload.messageId,
        role: 'assistant',
        content: event.payload.content,
        timestamp: event.payload.timestamp,
        model: event.payload.model,
        metadata: event.payload.metadata
      };

      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }
  }, [conversation]);

  /**
   * Handle AI error event
   */
  const handleAiError = useCallback((event) => {
    if (event.payload.conversationId === conversation?.id) {
      setError(event.payload.error);
      setIsTyping(false);
    }
  }, [conversation]);

  /**
   * Handle model change
   */
  const handleModelChange = useCallback(async (newModel) => {
    if (newModel !== currentModel && conversation) {
      try {
        setCurrentModel(newModel);

        // Notify service of model change
        eventBridgeService.publish('MODEL_CHANGED', {
          conversationId: conversation.id,
          newModel,
          userId,
          timestamp: new Date().toISOString()
        });

        // Notify parent component
        onModelChange?.(newModel);

        // Add system message about model change
        const systemMessage = {
          id: `system-${Date.now()}`,
          role: 'system',
          content: `Switched to ${newModel}. The conversation will continue with the new model.`,
          timestamp: new Date().toISOString(),
          isSystem: true
        };

        setMessages(prev => [...prev, systemMessage]);

      } catch (err) {
        setError(err.message);
      }
    }
  }, [currentModel, conversation, userId, onModelChange]);

  /**
   * Handle Enter key press
   */
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Format timestamp for display
   */
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Render message content with formatting
   */
  const renderMessageContent = (message) => {
    // Handle different message types
    if (message.isSystem) {
      return (
        <div className="system-message">
          <span className="system-icon">⚙️</span>
          {message.content}
        </div>
      );
    }

    if (message.isWelcome) {
      return (
        <div className="welcome-message">
          <span className="ai-icon">🤖</span>
          {message.content}
        </div>
      );
    }

    // Regular message formatting
    return (
      <div className="message-content">
        {message.content}
        {message.metadata && (
          <div className="message-metadata">
            <small>
              Model: {message.model} •
              Tokens: {message.metadata.tokenUsage} •
              Time: {message.metadata.processingTime}ms
            </small>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render typing indicator
   */
  const renderTypingIndicator = () => {
    if (!isTyping) return null;

    return (
      <div className="message ai-message typing-message">
        <div className="message-header">
          <span className="sender">AI Assistant</span>
          <span className="model-badge">{currentModel}</span>
        </div>
        <div className="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-interface">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-title">
          <h3>AI Travel Assistant</h3>
          {tripId && <span className="trip-indicator">Trip Context Active</span>}
        </div>
        <div className="chat-controls">
          <select
            value={currentModel}
            onChange={(e) => handleModelChange(e.target.value)}
            className="model-selector"
            disabled={isLoading}
          >
            <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            <option value="gpt-4">GPT-4</option>
            <option value="claude-3-sonnet">Claude 3 Sonnet</option>
            <option value="claude-3-opus">Claude 3 Opus</option>
            <option value="gemini-pro">Gemini Pro</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="chat-error">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Messages Container */}
      <div className="messages-container" ref={messagesContainerRef}>
        {isLoading && messages.length === 0 ? (
          <div className="chat-loading-state">
            <WaitingIndicator size="small" message="Starting conversation..." />
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.role}-message ${message.isSystem ? 'system' : ''} ${message.isWelcome ? 'welcome' : ''}`}
              >
                <div className="message-header">
                  <span className="sender">
                    {message.role === 'user' ? 'You' : 'AI Assistant'}
                  </span>
                  <span className="timestamp">
                    {formatTimestamp(message.timestamp)}
                  </span>
                  {message.model && (
                    <span className="model-badge">{message.model}</span>
                  )}
                </div>
                {renderMessageContent(message)}
              </div>
            ))}
            {renderTypingIndicator()}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="input-container">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={tripId
              ? "Ask me anything about your trip..."
              : "Ask me anything about travel planning..."
            }
            className="message-input"
            rows="2"
            disabled={isLoading || !conversation}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || !conversation}
            className="send-button"
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>

        <div className="input-hints">
          <span className="hint">Try asking: "What should I do on my first day?" or "Help me plan my itinerary"</span>
          {tripId && (
            <span className="context-indicator">
              💡 I have access to your current trip data
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;